import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import './CategoriasSeguridad.css';

export default function CategoriasSeguridad({ usuario }) {
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        icono: '',
        orden: 0
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchCategorias();
    }, []);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchCategorias = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/api/categorias-seguridad`
            );

            if (response.ok) {
                const data = await response.json();
                setCategorias(data);
                setError('');
            } else {
                setError('Error al cargar categorías');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Error de conexión al cargar categorías');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'orden' ? parseInt(value) || 0 : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombre.trim()) {
            setError('El nombre es obligatorio');
            return;
        }

        try {
            const isEditing = Boolean(editingId);
            const endpoint = isEditing
                ? `${API_BASE_URL}/api/categorias-seguridad/${editingId}`
                : `${API_BASE_URL}/api/categorias-seguridad`;
            const method = isEditing ? 'PUT' : 'POST';

            const body = { ...formData };

            const response = await fetchWithAuth(
                endpoint,
                {
                    method,
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(body)
                }
            );

            if (response.ok) {
                setSuccess(isEditing ? 'Categoría actualizada correctamente' : 'Categoría guardada correctamente');
                setFormData({ nombre: '', descripcion: '', icono: '', orden: 0 });
                setShowForm(false);
                setEditingId(null);
                fetchCategorias();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const data = await response.json();
                setError(data.error || 'Error al guardar categoría');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Error de conexión');
        }
    };

    const handleEdit = (categoria) => {
        setFormData({
            nombre: categoria.nombre,
            descripcion: categoria.descripcion || '',
            icono: categoria.icono || '',
            orden: categoria.orden || 0
        });
        setEditingId(categoria.id);
        setShowForm(true);
        setError('');
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({ nombre: '', descripcion: '', icono: '', orden: 0 });
        setError('');
    };

    const filteredCategorias = categorias.filter(cat =>
        cat.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!usuario || usuario.tipo === 'C') {
        return (
            <div className="container">
                <div style={{ textAlign: 'center', padding: '40px', color: '#d32f2f' }}>
                    <p>⚠️ Acceso denegado. No tienes permisos para acceder a esta sección.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container">
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Cargando categorías...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container categorias-container">
            <div className="header-section">
                <div className="header-content">
                    <h1>🏷️ Categorías de Seguridad</h1>
                    <p>Gestiona las categorías de artículos de seguridad y EPP</p>
                </div>
            </div>

            {error && (
                <div className="alert alert-error">
                    {error}
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    {success}
                </div>
            )}

            <div className="search-section">
                <div className="search-group">
                    <label className="search-label">Buscar Categorías</label>
                    <div className="search-wrapper">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Ej: Ropa, Casco..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>
                </div>
                <button
                    className="btn-nuevo-articulo"
                    onClick={() => {
                        setShowForm(true);
                        setEditingId(null);
                        setFormData({ nombre: '', descripcion: '', icono: '', orden: 0 });
                    }}
                >
                    <span>➕</span> Nueva Categoría
                </button>
            </div>

            {filteredCategorias.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📦</div>
                    <h3>No hay categorías registradas</h3>
                    <p>Comienza creando tu primera categoría de seguridad</p>
                    <button
                        className="btn-crear-primer-articulo"
                        onClick={() => setShowForm(true)}
                    >
                        <span className="btn-icon">➕</span>
                        <span className="btn-text">Crear Primera Categoría</span>
                    </button>
                </div>
            ) : (
                <div className="categorias-table-wrapper">
                    <table className="categorias-table">
                        <thead>
                            <tr>
                                <th>Icono</th>
                                <th>Nombre</th>
                                <th>Descripción</th>
                                <th>Orden</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategorias.map(categoria => (
                                <tr key={categoria.id || categoria.nombre}>
                                    <td className="icon-cell">{categoria.icono}</td>
                                    <td className="nombre-cell">{categoria.nombre}</td>
                                    <td className="descripcion-cell">{categoria.descripcion || '-'}</td>
                                    <td className="orden-cell">{categoria.orden}</td>
                                    <td className="estado-cell">
                                        <span className={`badge ${categoria.activa !== false ? 'badge-active' : 'badge-inactive'}`}>
                                            {categoria.activa !== false ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td className="acciones-cell">
                                        <button
                                            className="btn-edit"
                                            onClick={() => handleEdit(categoria)}
                                            title="Editar"
                                        >
                                            ✏️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && (
                <div className="modal-overlay" onClick={handleCancel}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingId ? 'Editar Categoría' : 'Nueva Categoría'}</h2>
                            <button className="btn-close" onClick={handleCancel}>✕</button>
                        </div>

                        <form onSubmit={handleSubmit} className="form-categorias">
                            <div className="form-group">
                                <label>Nombre *</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Ropa, Casco, Guantes..."
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Icono</label>
                                <input
                                    type="text"
                                    name="icono"
                                    value={formData.icono}
                                    onChange={handleInputChange}
                                    placeholder="Ej: 👕, 🪖, 🧤"
                                    maxLength="2"
                                />
                            </div>

                            <div className="form-group">
                                <label>Descripción</label>
                                <textarea
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleInputChange}
                                    placeholder="Descripción opcional..."
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label>Orden</label>
                                <input
                                    type="number"
                                    name="orden"
                                    value={formData.orden}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn-cancelar"
                                    onClick={handleCancel}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn-guardar-articulo"
                                >
                                    <span>✓</span> Guardar Categoría
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
}
